// Vorratsfoto: KI zählt sichtbare Einheiten, der Mensch bestätigt.
//
// Kernregel bleibt: Ein Foto darf Bestände erhöhen oder bestätigen. Was
// weniger sichtbar ist, wird NICHT automatisch abgebucht – hinten im
// Kühlschrank steht vielleicht noch was.
import type { ProductView } from '@speis/shared';
import { api } from '../core/api.js';
import { guard, guarded } from '../core/guard.js';
import { refreshExpiring } from '../core/sse.js';
import { applyProducts } from '../core/store.js';
import { clear, el } from '../ui/dom.js';
import { icon } from '../ui/icons.js';
import { openSheet } from '../ui/sheet.js';
import { toast } from '../ui/toast.js';
import { pickImage } from './image.js';

interface Suggestion {
  productId: number;
  name: string;
  currentClosed: number;
  visibleCount: number;
  confidence: string;
  action: 'increase' | 'confirm' | 'maybe_decrease';
  proposedClosed: number;
  apply: boolean;
  suggestShopping: boolean;
  suggestedBestBefore: string | null;
}

interface Row extends Suggestion {
  include: boolean;
  addToShopping: boolean;
  bestBefore: string | null;
}

const ACTION_TEXT: Record<Suggestion['action'], (s: Suggestion) => string> = {
  increase: (s) => `${s.visibleCount} gezählt (bisher ${s.currentClosed}) → erhöhen`,
  confirm: (s) => `${s.visibleCount} bestätigt`,
  maybe_decrease: (s) =>
    `nur ${s.visibleCount} sichtbar (Bestand ${s.currentClosed}) – evtl. verdeckt`,
};

export async function openPantryPhotoFlow(): Promise<void> {
  const image = await pickImage();
  if (!image) return;

  const loading = openSheet(
    el('div', { class: 'loading-block' }, [
      el('span', { class: 'spinner' }),
      el('p', { class: 'muted' }, 'Foto wird ausgewertet …'),
    ]),
    { title: 'Vorratsfoto', dismissable: false },
  );

  const result = await guard(
    () =>
      api<{ suggestions: Suggestion[] }>('/api/inventory/photo', {
        method: 'POST',
        body: { image },
      }),
    { failure: 'Auswertung fehlgeschlagen' },
  );
  loading.close();

  if (!result) return;
  if (!result.suggestions.length) {
    toast('Nichts Zählbares erkannt – die KI ordnet nur bekannten Katalogprodukten zu.');
    return;
  }
  showSuggestions(result.suggestions);
}

function showSuggestions(suggestions: Suggestion[]): void {
  const rows: Row[] = suggestions.map((s) => ({
    ...s,
    include: s.apply,
    addToShopping: s.suggestShopping,
    bestBefore: s.action === 'increase' ? s.suggestedBestBefore : null,
  }));

  const list = el('div', { class: 'receipt-list' });
  const body = el('div', {}, [
    el(
      'p',
      { class: 'field-hint' },
      'Bestätige, was passt. Was auf dem Foto fehlt, wird nicht automatisch abgebucht – nur auf ausdrücklichen Wunsch.',
    ),
    list,
  ]);
  const sheet = openSheet(body, { title: 'Foto-Abgleich' });

  function draw(): void {
    clear(list);
    for (const row of rows) list.append(rowEl(row));
  }

  function rowEl(row: Row): HTMLElement {
    const include = el(
      'button',
      {
        class: 'checkbox',
        type: 'button',
        role: 'checkbox',
        'aria-checked': String(row.include),
        'aria-label': `${row.name} übernehmen`,
        onclick: () => {
          row.include = !row.include;
          draw();
        },
      },
      icon('check', { size: 14 }),
    );

    const extras = el('div', { class: 'receipt-meta' });

    if (row.action === 'increase' && row.include) {
      const date = el('input', {
        class: 'input date-input',
        type: 'date',
        value: row.bestBefore ?? '',
        'aria-label': `Haltbarkeitsdatum für ${row.name}`,
      });
      date.addEventListener('change', () => {
        row.bestBefore = date.value || null;
      });
      extras.append(date);
    }

    if (row.action === 'maybe_decrease') {
      extras.append(
        el(
          'button',
          {
            class: 'chip',
            type: 'button',
            'aria-pressed': String(row.addToShopping),
            onclick: () => {
              row.addToShopping = !row.addToShopping;
              draw();
            },
          },
          [icon('cart', { size: 14 }), 'auf die Liste'],
        ),
      );
    }

    return el('div', { class: `receipt-row${row.include ? '' : ' is-excluded'}` }, [
      include,
      el('div', { class: 'receipt-main' }, [
        el('div', { class: 'item-name' }, row.name),
        el('div', { class: 'item-meta' }, [
          el('span', {}, ACTION_TEXT[row.action](row)),
          row.confidence === 'low' ? el('span', { class: 'badge badge-warn' }, 'unsicher') : null,
        ]),
        extras.childElementCount ? extras : null,
      ]),
    ]);
  }

  draw();

  sheet.addActions(
    el(
      'button',
      {
        class: 'btn btn-primary btn-block',
        type: 'button',
        onclick: guarded(
          async () => {
            const positions = rows
              .filter((r) => r.include || r.addToShopping)
              .map((r) => ({
                productId: r.productId,
                setClosed: r.include ? r.proposedClosed : null,
                addToShopping: r.addToShopping,
                bestBefore: r.bestBefore,
                confidence: r.confidence,
              }));

            if (!positions.length) {
              toast('Nichts ausgewählt');
              return;
            }

            const result = await api<{ products: ProductView[] }>('/api/inventory/photo/commit', {
              method: 'POST',
              body: { positions },
            });
            applyProducts(result.products);
            void refreshExpiring();
            sheet.close();
          },
          { success: 'Bestand aktualisiert', failure: 'Übernehmen fehlgeschlagen' },
        ),
      },
      'Übernehmen',
    ),
  );
}
